import { type RestMultiRowSurveyCard } from '@bbkAdminUtils/api-client/rest-cards-types';
import { type RestModalCard } from '@bbkAdminUtils/api-client/rest-modals-cards-types';
import { type Maybe } from '@bbkAdminUtils/utility-types';
import { LocalStorage } from '@bbkShared/LocalStorage';

type Props = {
  name: string;
  id: string;
  session: string;
};
type StoredState = {
  survey: RestMultiRowSurveyCard['provides'];
  modal: RestModalCard | undefined;
  showModal: boolean;
};
export class FunnelLocalState {
  LS = new LocalStorage('BrightbackLS');

  noReloadModalTemplates = [
    'modal_confirm_cancel',
    'modal_email_send',
    'modal_email_success',
    'final_offer',
  ];

  noReloadModalNames = [
    'post_cancel',
    'confirm_cancel_custom_data',
    'confirm_cancel',
  ];

  disabled: boolean;

  name: string;

  id: string;

  session: string;

  key: string;

  state: Partial<StoredState>;

  constructor({ name, id, session }: Props, disabled?: boolean) {
    this.disabled = disabled || session === `\${session_id}`;
    this.name = name;
    this.id = id;
    this.session = session;
    this.key = `BBLS-Funnel-${this.name}-${this.id}-${this.session}`;
    this.state = {};
  }

  didStateChange({ survey, modal, showModal }: Partial<StoredState>): boolean {
    return (
      this.state.survey !== survey ||
      this.state.modal !== modal ||
      this.state.showModal !== showModal
    );
  }

  saveState({ survey, modal, showModal }: StoredState): void {
    if (this.disabled) {
      return;
    }
    if (this.didStateChange({ survey, modal, showModal })) {
      // Save survey, modal, and modal state in LS for reload
      this.LS.put(this.key, {
        survey,
        modal,
        showModal,
      });
    }
  }

  subKey(namespace: string): string {
    return `${this.key}_${namespace}`;
  }

  has(namespace: string): boolean | undefined {
    if (this.disabled) {
      return false;
    }
    return this.LS.has(this.subKey(namespace))[1];
  }

  get<T = unknown>(namespace: string): Maybe<T> {
    if (this.disabled) {
      return undefined;
    }
    return this.LS.get(this.subKey(namespace))[1] as Maybe<T>;
  }

  del(namespace: string): undefined | [Error] | [null] {
    if (this.disabled) {
      return undefined;
    }
    return this.LS.del(namespace);
  }

  put(namespace: string, val: unknown): undefined | [Error] | [null, string] {
    if (this.disabled) {
      return undefined;
    }
    return this.LS.put(this.subKey(namespace), val);
  }

  search(namespace: string): { key?: string; value?: string }[] | null {
    if (this.disabled) {
      return null;
    }
    const reg = new RegExp(namespace);
    return this.LS.search(reg)[1];
  }

  hasSavedState(): boolean | undefined {
    if (this.disabled) {
      return false;
    }
    return this.LS.has(this.key)[1];
  }

  loadSavedState(): StoredState | null | undefined {
    if (this.hasSavedState()) {
      const [errG, savedState] = this.LS.get<StoredState>(this.key);
      if (!errG) {
        return this.cleanState(savedState);
      }
    }
    return null;
  }

  cleanState(state?: StoredState): StoredState | undefined {
    if (state) {
      const cleanState = { ...state };

      if (cleanState.survey) {
        cleanState.survey.confirmation = false;
        // zero these each time the page is loaded / refreshed
        cleanState.survey.responses = {};
      }

      if (cleanState.modal) {
        const { template, name } = cleanState.modal;
        if (
          (template && this.noReloadModalTemplates.includes(template)) ||
          (name && this.noReloadModalNames.includes(name))
        ) {
          cleanState.modal = {} as RestModalCard;
          cleanState.showModal = false;
        }
      }
      return cleanState;
    }
    return state;
  }
}
